import React, { useState } from 'react';
import { Card, Col, DatePicker, Row } from 'antd';
import { usePaymentByStatus, usePaymentData } from '@/modules/dashboard/hooks/usePaymentData';
import BarChartPagosPorMetodo from '@/modules/dashboard/components/BarChartPagosPorMetodo';
import PieChartPagosPorMetodo from '@/modules/dashboard/components/PieChartPagosPorMetodo';
import Text from 'antd/es/typography/Text';
import { showValueWithCurrency } from '@/utils/utils';
import { usePaymentHistogram } from '@/modules/dashboard/hooks/usePaymentHistogram';
import HistogramPagosPorFecha from '@/modules/dashboard/components/HistogramPagosPorFecha';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import PieChartPagosPorEstados from '@/modules/dashboard/components/PieChartPagosPorEstados';
import { useUser } from '@dofleini/security';
import UserQuantity from '@/modules/dashboard/components/UserQuantity';
import PropTypes from 'prop-types';

dayjs.extend(weekday);
dayjs.extend(localeData);

const { RangePicker } = DatePicker;

const now = new Date();
const startOfMonth = new Date(now.getFullYear(), 0, 1);
const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

const DashboardPage = ({ userId }) => {
  
  const { hasPermission } = useUser();
  const [selectedRange, setSelectedRange] = useState([
    dayjs(startOfMonth),
    dayjs(endOfMonth),
  ]);
  const [range, setRange] = useState({
    type: 'RANGE',
    field: 'createdAt',
    from: new Date(startOfMonth.setHours(0, 0, 0, 0)).toISOString(),
    to: new Date(endOfMonth.setHours(23, 59, 59, 999)).toISOString(),
    isDate: true,
  });
  const { data: paymentData } = usePaymentData(range, userId);
  const { data: paymentDataByStatus } = usePaymentByStatus(range, userId);
  const { data: histogramData } = usePaymentHistogram(range, userId);
  
  const isAdmin = hasPermission('ADMIN');
  
  // Extract payment summary data safely
  const totalPayments = paymentData?.totalPayments || {};
  const anticipados = paymentData.anticipated || {};
  const bonifications = paymentData.bonifications || {};
  
  return (
    <div style={{ padding: 24 }}>
      <Card style={{ marginBottom: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <RangePicker
          allowClear={false}
          value={selectedRange}
          onChange={(dates) => {
            if (dates && dates.length === 2) {
              setSelectedRange(dates);
              const [start, end] = dates;
              const from = new Date(start.year(), start.month(), start.date(), 0, 0, 0, 0).toISOString();
              const to = new Date(end.year(), end.month(), end.date(), 23, 59, 59, 999).toISOString();
              setRange({
                filters: {
                  type: 'RANGE',
                  field: 'createdAt',
                  from,
                  to,
                  isDate: true,
                },
              });
            }
          }}
        />
      </Card>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {isAdmin && <Col xs={24} md={6}>
          <UserQuantity range={range} />
        </Col>}
        <Col xs={24} md={isAdmin ? 6 : 8}>
          <Card title={'Total de pagos e importes'} bodyStyle={{ padding: '10px 24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginTop: 6 }}>
              <div>
                <Text strong style={{ fontSize: '30px' }}>{totalPayments.totalCount ?? 0}</Text>
              </div>
              <div>
                <Text>
                  {showValueWithCurrency(totalPayments.totalAmount ?? 0)}
                </Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={isAdmin ? 6 : 8}>
          <Card title={'Pagos anticipados e importe'} bodyStyle={{ padding: '10px 24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div>
                <Text strong style={{ fontSize: '30px' }}>{anticipados.anticipatedCount ?? 0}</Text>
              </div>
              <div>
                <Text>
                  {showValueWithCurrency(anticipados.anticipatedAmount ?? 0)}
                </Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={isAdmin ? 6 : 8}>
          <Card title={'Monto en bonificaciones'} bodyStyle={{ padding: '10px 24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div>
                <Text strong style={{ fontSize: '30px' }}>
                  {showValueWithCurrency(bonifications.bonification || '0')}</Text>
              </div>
              {isAdmin && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>
                  Total: {showValueWithCurrency(bonifications.totalAmount || '0')}
                </Text>
                <Text>
                  Pagado: {showValueWithCurrency(bonifications.totalPayed || '0')}
                </Text>
              </div>}
            </div>
          </Card>
        </Col>
      </Row>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <Card title="Pagos por estados" bodyStyle={{ padding: '10px 24px' }}>
            <PieChartPagosPorEstados data={paymentDataByStatus || []} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title="Cantidad de pagos" bodyStyle={{ padding: '10px 24px' }}>
            <PieChartPagosPorMetodo data={paymentData?.byPaymentMethods || []} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title="Importe de pagos" bodyStyle={{ padding: '10px 24px' }}>
            <BarChartPagosPorMetodo data={paymentData?.byPaymentMethods || []} />
          </Card>
        </Col>
      </Row>
      
      <Card title="Pagos realizados" bodyStyle={{ padding: '10px 24px' }}>
        <HistogramPagosPorFecha data={histogramData || []} />
      </Card>
    </div>
  );
};

DashboardPage.propTypes = {
  userId: PropTypes.string,
};


export default DashboardPage;
